/**
 * Maintenance data migration utilities
 * Direct localStorage access - NO React imports
 * Handles migration from old camelCase keys to canonical snake_case
 * Now supports collections with vessel scoping
 */
import { metaKey } from '../metaKey';

// ENV GUARD for SSR/test environments
const hasLS = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * Read legacy maintenance data from old keys
 * @param {string} vesselId - The vessel ID to read data for
 * @returns {Object} Legacy history and equipment arrays
 */
export function readLegacyMaintenance(vesselId) {
  if (!hasLS) return { history: [], equipment: [] };
  
  try {
    const h = localStorage.getItem(`maintenanceHistory_${vesselId}`);
    const e = localStorage.getItem(`equipment_${vesselId}`);
    return {
      history: h ? JSON.parse(h) : [],
      equipment: e ? JSON.parse(e) : []
    };
  } catch (err) {

    return { history: [], equipment: [] };
  }
}

/**
 * Clear legacy maintenance keys from localStorage
 * @param {string} vesselId - The vessel ID to clear data for
 */
export function clearLegacyMaintenance(vesselId) {
  if (!hasLS) return;
  
  localStorage.removeItem(`maintenanceHistory_${vesselId}`);
  localStorage.removeItem(`equipment_${vesselId}`);
}

/**
 * Check if migration has already been performed for this vessel
 * Now supports collections with vessel scoping
 * @param {string} vesselId - The vessel ID to check
 * @param {Object} collections - Collections for data access (optional)
 * @returns {boolean|Promise<boolean>} True if migration already done
 */
export function getMigrationFlag(vesselId, collections = null) {
  if (collections) {
    // Return promise for collections path
    return (async () => {
      try {
        const migrationKey = metaKey('migration_maint_v2', vesselId);
        const record = await collections.get('meta', migrationKey);
        return record?.data === true;
      } catch (error) {

        return hasLS ? localStorage.getItem(`migration_maint_v2_${vesselId}`) === '1' : false;
      }
    })();
  }
  
  // Sync localStorage fallback
  if (!hasLS) return false;
  return localStorage.getItem(`migration_maint_v2_${vesselId}`) === '1';
}

/**
 * Set the migration flag to prevent duplicate migrations
 * Now supports collections with vessel scoping
 * @param {string} vesselId - The vessel ID to mark as migrated
 * @param {Object} collections - Collections for data access (optional)
 */
export async function setMigrationFlag(vesselId, collections = null) {
  if (collections) {
    try {
      const { createBaseRecord } = await import('../../types/core');
      const migrationKey = metaKey('migration_maint_v2', vesselId);
      await collections.upsert('meta', createBaseRecord({
        id: migrationKey,
        vesselId,
        data: true,
        origin: 'migration'
      }));
      return;
    } catch (error) {

    }
  }
  
  // Fallback to localStorage
  if (!hasLS) return;
  localStorage.setItem(`migration_maint_v2_${vesselId}`, '1');
}